<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'

import MobileTabBar, { type MobileTabItem } from '@/components/MobileTabBar.vue'
import SubPageHeader from '@/components/SubPageHeader.vue'
import { provideSubPageHeaderAction } from '@/composables/useSubPageHeaderAction'
import { withReturnTarget } from '@/router/returnNavigation'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const ownedSubPageAction = provideSubPageHeaderAction()

const canUseShipping = computed(() => auth.canShip)
const canUseReceiving = computed(() => auth.canReceive)
const canUseFinance = computed(() => auth.canFinance)
const canUseWarehouseWork = computed(() => auth.canUseWarehouseWork)
const canSelfOperate = computed(() => auth.canSelfOperate)

const items = computed<MobileTabItem[]>(() => {
  const navigation: MobileTabItem[] = []
  if (canUseShipping.value) {
    navigation.push(
      { label: t('nav.home'), to: '/customer', icon: 'home' },
      { label: t('nav.declarations'), to: '/declarations', icon: 'order-list' },
    )
  }
  if (canUseReceiving.value && !canUseShipping.value) {
    navigation.push({ label: t('nav.home'), to: '/receiving', icon: 'home' })
  }
  if (canUseReceiving.value) {
    navigation.push({ label: t('nav.receivingOrders'), to: '/receiving/tasks', icon: 'task' })
  }
  if (canUseFinance.value && !canUseShipping.value) {
    navigation.push({ label: t('payment.recordsTitle'), to: '/payments', icon: 'wallet' })
  }
  if (canUseWarehouseWork.value) {
    navigation.push({ label: t('nav.work'), to: '/work', icon: 'task' })
  }
  if (canSelfOperate.value) {
    navigation.push({ label: t('nav.selfOperation'), to: '/self-operation', icon: 'task' })
  }
  // 现代收货员同时具备客户成员权限:首页/我的申报/收货/我的。
  // 旧兼容纯收货账号保留收货/我的；已收记录收进收货工作台，不再独占 tab。
  navigation.push({ label: t('nav.profile'), to: '/profile', icon: 'user' })
  return navigation
})

const immersiveHome = computed(() => ['customer-home', 'receiving-home'].includes(String(route.name)))
const navigationActivePath = computed(() => (
  route.name === 'receiving-home' && canUseShipping.value ? '/customer' : route.path
))

const isSubPage = computed(() => route.meta.subPage === true)
const subPageTitle = computed(() => (typeof route.meta.title === 'string' ? t(route.meta.title) : ''))
interface ResolvedSubPageAction {
  label: string
  to: RouteLocationRaw | undefined
  icon: 'order-list' | undefined
  disabled: boolean
  onSelect: (() => void | Promise<void>) | undefined
}
const subPageAction = computed<ResolvedSubPageAction | undefined>(() => {
  if (ownedSubPageAction.value) {
    return {
      ...ownedSubPageAction.value,
      to: undefined,
      icon: ownedSubPageAction.value.icon === 'order-list' ? 'order-list' : undefined,
      disabled: ownedSubPageAction.value.disabled ?? false,
    }
  }
  const action = route.meta.headerAction as { label?: string; to?: string; icon?: 'order-list' } | undefined
  if (!action?.label) return undefined
  return {
    label: t(action.label),
    to: action.to ? withReturnTarget(router, action.to, route.fullPath) : undefined,
    icon: action.icon,
    disabled: false,
    onSelect: undefined,
  }
})

function selectSubPageAction(): void {
  void subPageAction.value?.onSelect?.()
}

</script>

<template>
  <div class="app-shell">
    <SubPageHeader
      v-if="isSubPage"
      :action-icon="subPageAction?.icon"
      :action-label="subPageAction?.label"
      :action-to="subPageAction?.to"
      :action-disabled="subPageAction?.disabled"
      :back-to="route.meta.backTo"
      :title="subPageTitle"
      @action="selectSubPageAction"
    />

    <main
      id="main-content"
      class="app-content"
      :class="{ 'app-content--immersive': immersiveHome, 'app-content--subpage': isSubPage }"
      tabindex="-1"
    >
      <RouterView />
    </main>

    <MobileTabBar v-if="!isSubPage" :active-path="navigationActivePath" :items="items" />
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100dvh;
}

.app-content {
  width: min(100%, 960px);
  margin: 0 auto;
  padding: var(--space-5) var(--page-gutter) calc(92px + env(safe-area-inset-bottom));
}

.app-content--immersive {
  width: 100%;
  max-width: none;
  padding: 0 0 calc(80px + env(safe-area-inset-bottom));
}

/* 子页面无底部 tabbar,去掉其占位 padding */
.app-content--subpage {
  padding-bottom: var(--space-5);
}
</style>
